/*
 *
 * Chat reducer
 *
 */
import produce from 'immer';
import {
  GET_CONVERSATION_LIST,
  GET_CONVERSATION_DETAIL,
  REMOVE_CONVERSATION_DETAIL,
  UPDATE_CONVERSATION_MESSAGES,
  CONVERSATION_LIST_TYPES,
  GET_INBOX_DETAIL,
  GET_CONVERSATION_LIST_FILTERED,
  GET_TEAM_MEMBERS,
  GET_CONVERSATION_MEMBERS,
  UPDATE_CONVERSATION_LIST,
  UPDATE_INBOX_DETAIL_STATISTICS,
  CHANGE_CURRENT_CONVERSATION,
  ADD_NEW_MEMBER_CONVERSATION,
  GET_USER_STATUS,
  UPDATE_USER_STATUS,
  LOADING_NEW_CONVERSATION,
  GET_CONVERSATION_FROM_PARAMS,
  CLOSE_SELECTED_CONVERSATION,
  SET_CONVERSATION_REQUEST,
  UPDATE_SENDING_MESSAGES,
  UPDATE_MESSAGE_READ_STATUS,
  UPDATE_MESSAGE_DELIVERED,
  IS_UPDATE_CONVERSATION_LISTS,
  UPDATE_CONVERSATION_DETAILS,
  GET_CANNED_MESSAGES,
  SAVE_CANNED_MESSAGE,
  DELETE_CANNED_MESSAGE,
  EDITOR_HAS_CONTENT,
  HAS_NEW_MESSAGES,
  HAS_SEND_MESSAGE,
  SET_CUSTOM_FILTER,
  CHANGE_ZENDESK_INBOX_ACTIVE_TAB,
  CREATE_ZENDESK_TICKET,
  GET_KNOWLEDGE_BASE_ARTICLES,
  GET_CONNECTED_APPS,
  UPDATE_INBOX_STATISTICS,
  UPDATE_CONVERSATIONS_READ,
  UPDATE_SELECTED_FILTERS,
  CREATE_SERVICE_CLOUD_CASE,
  GET_SERVICE_CLOUD_ARTICLES,
  CHANGE_SERVICE_CLOUD_ACTIVE_TAB,
  BLOCK_VISITOR,
  OPEN_VISITOR_BLOCK_MODAL,
  GET_TAGS,
  GET_SUBSCRIPTION_DETAILS,
  GET_HIGHLIGHTS_FIELDS,
  GET_HIGHLIGHTS_TEMPLATES,
  SAVE_HIGHLIGHTS_BUILDER,
  UPDATE_SELECTED_FIELDS_COUNT,
  UPDATE_CONVERSATION_LIST_FILTERS,
  GET_ATTACHMENTS,
  UPDATE_ATTACHMENTS,
  UPDATE_UPLOAD_PROGRESS,
} from './constants';

export const initialState = {
  loading: true,
  inbox: {
    pusherChannels: {},
    statistics: {},
    sender: {},
    loading: false,
    error: false,
  },
  conversationList: {
    visitors: [],
    visitorNext: Date.now(),
    bot: [],
    botNext: Date.now(),
    loading: true,
    error: false,
    filtering: false,
    updateConversationList: {
      status: false,
      type: '',
    },
    customFilter: {
      0: { from: '', to: '' },
      1: { from: '', to: '' },
    },
    logs: {
      selectedFilters: {
        bot: null,
        nonBot: null,
      },
      conversationsRead: {},
    },
    filters: {
      0: {
        conversationType: '',
        visitorActivity: '',
        visitorTags: [],
      },
      1: {
        conversationType: '',
        visitorActivity: '',
        visitorTags: [],
      },
    },
  },
  conversationDetail: {
    loading: false,
    loadingNewConversation: false,
    error: false,
    messages: [],
    sendingMessages: [],
    lastMessagePos: '',
    visitor: {},
    conversationId: '',
    details: { visitorLastReadTime: 0, visitorLastDeliveredTime: 0 },
    participants: {
      appUsers: [],
    },
  },
  team: {
    loading: false,
    error: false,
    users: [],
  },
  addNewMember: {
    loading: false,
    error: false,
  },
  currentConversation: {},
  userStatus: false,
  cannedMessages: {
    data: [],
    isSaving: false,
    isLoading: false,
    error: false,
    newMessage: false,
    hasSendMessage: false,
  },
  editorHasContent: false,
  zendeskInboxActiveTab: 1,
  zendeskTicket: {
    loading: false,
    ticketId: '',
    error: '',
  },
  knowledgeBaseArticles: {
    loading: false,
    data: [],
    error: '',
  },
  connectedApps: [],
  serviceCloud: {
    tab: 1,
    caseCreation: {
      loading: false,
      caseId: '',
      error: '',
    },
    articles: {
      loading: false,
      data: [],
      error: '',
    },
  },
  tags: {
    loading: false,
    data: [],
    error: '',
  },
  highlights: {
    isHighlightsSetup: false,
    isHighlightsInitialSetup: false,
    fields: {
      loading: false,
      data: [],
      error: '',
    },
    templates: {
      loading: false,
      data: [],
      error: '',
    },
    builder: {
      loading: false,
      data: [],
      error: '',
    },
    showPopup: {
      salesforce: false,
      hubspot: false,
      intent: false,
      technologies: false,
    },
    menu: {
      loading: false,
      data: [],
      error: '',
    },
    selectedFieldsCount: {
      ziChat: 0,
      salesforce: 0,
      hubspot: 0,
      contact: 0,
      company: 0,
      intent: 0,
      technologies: 0,
    },
  },
  subscriptions: {
    loading: false,
    data: {
      hasIntent: false,
      hasScoops: false,
      hasTechnologies: false,
    },
    error: '',
  },
  integrations: {
    loading: false,
    data: {
      salesforce: false,
      hubspot: false,
    },
  },
  blockVisitor: {
    loading: false,
    data: {
      isBlocked: false,
      showModal: false,
    },
    error: '',
  },
  attachments: {
    loading: false,
    data: {
      visitorAttachments: [],
      accountAttachments: [],
    },
    error: '',
    uploadProgress: {},
  },
};

/* eslint-disable default-case, no-param-reassign */
const chatReducer = (state = initialState, action) => {
  return produce(state, draft => {
    switch (action.type) {
      case SET_CUSTOM_FILTER:
        draft.conversationList.customFilter[action.metadata.activeTab] = action.payload.data;
        break;

      case SET_CONVERSATION_REQUEST.START:
        draft.inbox.loading = true;
        draft.inbox.error = false;
        break;

      case SET_CONVERSATION_REQUEST.SUCCESS:
        draft.inbox.loading = false;
        draft.inbox.error = false;
        break;

      case SET_CONVERSATION_REQUEST.ERROR:
        draft.inbox.loading = false;
        draft.inbox.error = action.error;
        break;

      case CLOSE_SELECTED_CONVERSATION.START:
        draft.conversationList.loading = true;
        draft.conversationList.error = false;
        break;

      case CLOSE_SELECTED_CONVERSATION.SUCCESS:
        draft.conversationList.loading = false;
        draft.conversationList.error = false;
        break;

      case CLOSE_SELECTED_CONVERSATION.ERROR:
        draft.conversationList.loading = false;
        draft.conversationList.error = action.error;
        break;

      case GET_INBOX_DETAIL.START:
        draft.inbox.loading = true;
        draft.inbox.error = false;
        break;

      case GET_INBOX_DETAIL.SUCCESS:
        draft.inbox.loading = false;
        draft.inbox.error = false;
        draft.loading = false;
        draft.inbox.pusherChannels = action.payload.data.pusherChannels;
        draft.inbox.sender = action.payload.data.sender;
        draft.inbox.statistics = action.payload.data.statistics;
        break;

      case GET_INBOX_DETAIL.ERROR:
        draft.inbox.loading = false;
        draft.inbox.error = action.error;
        break;

      case UPDATE_INBOX_STATISTICS.SUCCESS:
        draft.inbox.statistics = action.payload.statistics;
        break;

      case UPDATE_INBOX_DETAIL_STATISTICS:
        draft.inbox.statistics = action.payload.data.statistics;
        break;

      case GET_TEAM_MEMBERS.START:
        draft.team.loading = true;
        draft.team.error = false;
        break;

      case GET_TEAM_MEMBERS.SUCCESS:
        draft.team.loading = false;
        draft.team.users = action.payload.data.users;
        draft.team.error = false;
        break;

      case GET_TEAM_MEMBERS.ERROR:
        draft.team.loading = false;
        draft.team.error = action.error;
        break;

      case GET_CONVERSATION_MEMBERS.SUCCESS:
        draft.conversationDetail.participants = action.payload.data;
        break;

      case GET_CONVERSATION_LIST.START:
        draft.conversationList.loading = true;
        draft.conversationList.error = false;
        break;

      case UPDATE_CONVERSATIONS_READ.START:
        draft.conversationList.logs.conversationsRead = {};
        break;

      case UPDATE_CONVERSATIONS_READ.SUCCESS:
        draft.conversationList.logs.conversationsRead = action.payload.conversationsRead;
        break;

      case UPDATE_SELECTED_FILTERS.START:
        draft.conversationList.logs.selectedFilters = {
          bot: null,
          nonBot: null,
        };
        break;

      case UPDATE_SELECTED_FILTERS.SUCCESS:
        draft.conversationList.logs.selectedFilters = action.payload.selectedFilters;
        break;

      case REMOVE_CONVERSATION_DETAIL:
        draft.conversationDetail.messages = [];
        draft.conversationDetail.lastMessagePos = null;
        draft.conversationDetail.details = {};
        draft.conversationDetail.visitor = {};
        draft.conversationDetail.sendingMessages = [];
        break;

      case GET_CONVERSATION_LIST_FILTERED.START:
        draft.conversationList.filtering = true;
        break;

      case GET_CONVERSATION_LIST_FILTERED.SUCCESS:
        draft.conversationList.filtering = false;
        break;

      case GET_CONVERSATION_LIST_FILTERED.ERROR:
        draft.conversationList.filtering = false;
        break;

      case GET_CONVERSATION_LIST.SUCCESS:
        draft.conversationList.loading = false;
        draft.conversationList.error = false;
        draft.conversationList.updateConversationList.status = false;
        draft.conversationList.updateConversationList.type = '';

        switch (action.payload.data.type) {
          case CONVERSATION_LIST_TYPES.BOT: {
            draft.conversationList.bot = action.payload.data.conversations;
            draft.conversationList.botNext = action.payload.data.next;
            break;
          }
          case CONVERSATION_LIST_TYPES.NON_BOT: {
            draft.conversationList.visitors = action.payload.data.conversations;
            draft.conversationList.visitorNext = action.payload.data.next;
            break;
          }
          case CONVERSATION_LIST_TYPES.ME: {
            draft.conversationList.visitors = action.payload.data.conversations;
            draft.conversationList.visitorNext = action.payload.data.next;
            break;
          }
          default: {
            return;
          }
        }
        break;

      case GET_CONVERSATION_LIST.ERROR:
        draft.conversationList.loading = false;
        draft.conversationList.error = action.error;
        break;

      case GET_CONVERSATION_DETAIL.START:
        draft.conversationDetail.loading = true;
        draft.conversationDetail.error = false;
        draft.conversationDetail.loadingNewConversation = false;
        break;

      case LOADING_NEW_CONVERSATION:
        draft.conversationDetail.loadingNewConversation = true;
        break;

      case GET_CONVERSATION_DETAIL.SUCCESS:
        draft.conversationDetail.loading = false;
        draft.conversationDetail.loadingNewConversation = false;
        draft.conversationDetail.error = false;
        draft.conversationDetail.messages = action.payload.data.messages;
        draft.conversationDetail.lastMessagePos = action.payload.data.lastMessagePos;
        draft.conversationDetail.visitor = action.payload.data.visitor;
        draft.conversationDetail.conversationId = action.payload.data.conversationId;
        draft.conversationDetail.details =
          typeof action.payload.data.details === 'object' &&
          !Array.isArray(action.payload.data.details)
            ? action.payload.data.details
            : {};
        break;

      case UPDATE_CONVERSATION_DETAILS:
        draft.conversationDetail.details = {
          ...draft.conversationDetail.details,
          initiateFeedback: false,
        };
        break;

      case GET_CONVERSATION_DETAIL.ERROR:
        draft.conversationDetail.loading = false;
        draft.conversationDetail.loadingNewConversation = false;
        draft.conversationDetail.error = action.error;
        break;

      case GET_CONVERSATION_FROM_PARAMS.START:
        draft.conversationDetail.loadingNewConversation = true;
        break;

      case GET_CONVERSATION_FROM_PARAMS.SUCCESS:
      case GET_CONVERSATION_FROM_PARAMS.ERROR:
        draft.conversationDetail.loadingNewConversation = false;
        break;

      case UPDATE_CONVERSATION_MESSAGES.SUCCESS:
        draft.conversationDetail.messages = action.payload.data.messages;
        break;

      case UPDATE_CONVERSATION_LIST:
        if (action.payload.data.type === CONVERSATION_LIST_TYPES.BOT) {
          draft.conversationList.bot = action.payload.data.list;
        } else if (
          action.payload.data.type === CONVERSATION_LIST_TYPES.NON_BOT ||
          action.payload.data.type === CONVERSATION_LIST_TYPES.ME
        ) {
          draft.conversationList.visitors = action.payload.data.list;
        }
        break;

      case ADD_NEW_MEMBER_CONVERSATION.START:
        draft.addNewMember.loading = true;
        break;
      case ADD_NEW_MEMBER_CONVERSATION.SUCCESS:
        draft.addNewMember.loading = false;
        break;
      case ADD_NEW_MEMBER_CONVERSATION.ERROR:
        draft.addNewMember.loading = false;
        break;
      case CHANGE_CURRENT_CONVERSATION:
        draft.currentConversation = action.metadata.currentConversation;
        break;

      case GET_USER_STATUS.SUCCESS:
        draft.userStatus = action.payload.data.available;
        break;

      case UPDATE_USER_STATUS.SUCCESS:
        draft.userStatus = action.payload.data.available;
        break;

      case UPDATE_SENDING_MESSAGES:
        draft.conversationDetail.sendingMessages = action.payload.data.messages;
        break;

      case UPDATE_MESSAGE_READ_STATUS:
        draft.conversationDetail.details.visitorLastReadTime = action.payload.data;
        break;

      case UPDATE_MESSAGE_DELIVERED:
        draft.conversationDetail.details.visitorLastDeliveredTime = action.payload.data;
        break;

      case IS_UPDATE_CONVERSATION_LISTS:
        draft.conversationList.updateConversationList.status = action.payload.data;
        draft.conversationList.updateConversationList.type = action.payload.type;
        break;

      case GET_CANNED_MESSAGES.START:
        draft.cannedMessages.isLoading = true;
        draft.cannedMessages.error = false;
        break;
      case GET_CANNED_MESSAGES.SUCCESS:
        draft.cannedMessages.isLoading = false;
        draft.cannedMessages.data = action.payload.data;
        draft.cannedMessages.error = false;
        break;
      case GET_CANNED_MESSAGES.ERROR:
        draft.cannedMessages.isLoading = false;
        draft.cannedMessages.error = true;
        break;
      case SAVE_CANNED_MESSAGE.START:
        draft.cannedMessages.isSaving = true;
        draft.cannedMessages.error = false;
        break;
      case SAVE_CANNED_MESSAGE.SUCCESS:
        draft.cannedMessages.isSaving = false;
        draft.cannedMessages.error = false;
        draft.cannedMessages.data = action.payload.data;
        break;
      case SAVE_CANNED_MESSAGE.ERROR:
        draft.cannedMessages.isSaving = false;
        draft.cannedMessages.error = true;
        break;
      case DELETE_CANNED_MESSAGE.START:
        draft.cannedMessages.error = false;
        break;
      case DELETE_CANNED_MESSAGE.SUCCESS:
        draft.cannedMessages.data = action.payload.data;
        draft.cannedMessages.error = false;
        break;
      case DELETE_CANNED_MESSAGE.ERROR:
        draft.cannedMessages.error = true;
        break;
      case EDITOR_HAS_CONTENT:
        draft.editorHasContent = action.metadata.value;
        break;
      case HAS_NEW_MESSAGES:
        draft.cannedMessages.newMessage = action.payload.data;
        break;
      case HAS_SEND_MESSAGE.SUCCESS:
        draft.cannedMessages.hasSendMessage = action.payload.data;
        break;
      case CHANGE_ZENDESK_INBOX_ACTIVE_TAB:
        draft.zendeskInboxActiveTab = action.tabIndex;
        break;
      case CREATE_ZENDESK_TICKET.START:
        draft.zendeskTicket.loading = true;
        draft.knowledgeBaseArticles.error = '';
        break;
      case CREATE_ZENDESK_TICKET.SUCCESS:
        draft.zendeskTicket.loading = false;
        draft.zendeskTicket.ticketId = action.payload.data.ticketId;
        draft.zendeskTicket.error = '';
        break;
      case CREATE_ZENDESK_TICKET.ERROR:
        draft.zendeskTicket.loading = false;
        draft.zendeskTicket.ticketId = '';
        draft.zendeskTicket.error = action.errorMessage;
        break;
      case GET_KNOWLEDGE_BASE_ARTICLES.START:
        draft.knowledgeBaseArticles.loading = true;
        draft.knowledgeBaseArticles.data = [];
        draft.knowledgeBaseArticles.error = '';
        break;
      case GET_KNOWLEDGE_BASE_ARTICLES.SUCCESS:
        draft.knowledgeBaseArticles.loading = false;
        draft.knowledgeBaseArticles.data = action.payload.data;
        draft.knowledgeBaseArticles.error = '';
        break;
      case GET_KNOWLEDGE_BASE_ARTICLES.ERROR:
        draft.knowledgeBaseArticles.loading = false;
        draft.knowledgeBaseArticles.data = [];
        draft.knowledgeBaseArticles.error = action.errorMessage;
        break;
      case GET_CONNECTED_APPS.START:
        draft.connectedApps = [];
        break;
      case GET_CONNECTED_APPS.SUCCESS:
        if (action.payload.data.includes('salesforce')) {
          draft.integrations.data.salesforce = true;
        }
        if (action.payload.data.includes('hubspot')) {
          draft.integrations.data.hubspot = true;
        }
        draft.connectedApps = action.payload.data;
        break;
      case GET_CONNECTED_APPS.ERROR:
        draft.connectedApps = [];
        break;
      case CHANGE_SERVICE_CLOUD_ACTIVE_TAB:
        draft.serviceCloud.tab = action.tabIndex;
        break;
      case CREATE_SERVICE_CLOUD_CASE.START:
        draft.serviceCloud.caseCreation.loading = true;
        draft.serviceCloud.caseCreation.error = '';
        break;
      case CREATE_SERVICE_CLOUD_CASE.SUCCESS:
        draft.serviceCloud.caseCreation.loading = false;
        draft.serviceCloud.caseCreation.caseId = action.payload.data.caseId;
        draft.serviceCloud.caseCreation.error = '';
        break;
      case CREATE_SERVICE_CLOUD_CASE.ERROR:
        draft.serviceCloud.caseCreation.loading = false;
        draft.serviceCloud.caseCreation.caseId = '';
        draft.serviceCloud.caseCreation.error = action.errorMessage;
        break;
      case GET_SERVICE_CLOUD_ARTICLES.START:
        draft.serviceCloud.articles.loading = true;
        draft.serviceCloud.articles.data = [];
        draft.serviceCloud.articles.error = '';
        break;
      case GET_SERVICE_CLOUD_ARTICLES.SUCCESS:
        draft.serviceCloud.articles.loading = false;
        draft.serviceCloud.articles.data = action.payload.data;
        draft.serviceCloud.articles.error = '';
        break;
      case GET_SERVICE_CLOUD_ARTICLES.ERROR:
        draft.serviceCloud.articles.loading = false;
        draft.serviceCloud.articles.data = [];
        draft.serviceCloud.articles.error = action.errorMessage;
        break;
      case BLOCK_VISITOR.START:
        draft.blockVisitor.loading = true;
        break;

      case BLOCK_VISITOR.SUCCESS:
        draft.blockVisitor.loading = false;
        draft.blockVisitor.data.isBlocked = action.payload.data.isBlocked;
        break;

      case BLOCK_VISITOR.ERROR:
        draft.blockVisitor.loading = false;
        draft.blockVisitor.error = action.error;
        break;

      case OPEN_VISITOR_BLOCK_MODAL:
        draft.blockVisitor.data.showModal = action.payload.showModal;
        break;
      case GET_TAGS.START:
        draft.tags.loading = true;
        draft.tags.error = '';
        break;
      case GET_TAGS.SUCCESS:
        draft.tags.loading = false;
        draft.tags.data = action.payload.data;
        break;
      case GET_TAGS.ERROR:
        draft.tags.loading = false;
        draft.tags.error = action.error;
        break;
      case GET_SUBSCRIPTION_DETAILS.START:
        draft.subscriptions.loading = true;
        draft.subscriptions.error = '';
        break;
      case GET_SUBSCRIPTION_DETAILS.SUCCESS:
        draft.subscriptions.loading = false;
        draft.subscriptions.data.hasIntent = action.payload.data.hasIntent;
        draft.subscriptions.data.hasTechnologies = action.payload.data.hasTechnologies;
        draft.subscriptions.error = '';
        break;
      case GET_SUBSCRIPTION_DETAILS.ERROR:
        draft.subscriptions.loading = false;
        draft.subscriptions.error = action.errorMessage;
        break;
      case GET_HIGHLIGHTS_FIELDS.START:
        draft.highlights.fields.loading = true;
        draft.highlights.fields.data = [];
        draft.highlights.fields.error = '';
        break;
      case GET_HIGHLIGHTS_FIELDS.SUCCESS:
        draft.highlights.fields.loading = false;
        draft.highlights.isHighlightsSetup = action.payload.data.isHighlightsSetup;
        draft.highlights.showPopup.salesforce = !action.payload.data.hasSalesforce;
        draft.highlights.showPopup.hubspot = !action.payload.data.hasHubspot;
        draft.highlights.showPopup.intent = !action.payload.data.hasIntent;
        draft.highlights.showPopup.technologies = !action.payload.data.hasTechnology;
        draft.highlights.fields.data = action.payload.data.highlights;
        draft.highlights.builder.data = action.payload.data.highlights;
        break;
      case GET_HIGHLIGHTS_FIELDS.ERROR:
        draft.highlights.fields.loading = false;
        draft.highlights.fields.error = action.error;
        break;
      case GET_HIGHLIGHTS_TEMPLATES.START:
        draft.highlights.templates.loading = true;
        draft.highlights.menu.loading = true;
        draft.highlights.templates.data = [];
        draft.highlights.menu.data = [];
        draft.highlights.templates.error = '';
        draft.highlights.menu.error = '';
        break;
      case GET_HIGHLIGHTS_TEMPLATES.SUCCESS:
        draft.highlights.templates.loading = false;
        draft.highlights.menu.loading = false;
        draft.highlights.templates.data = action.payload.data.templates;
        draft.highlights.menu.data = action.payload.data.highlightsMenu;
        break;
      case GET_HIGHLIGHTS_TEMPLATES.ERROR:
        draft.highlights.templates.loading = false;
        draft.highlights.menu.loading = false;
        draft.highlights.templates.error = action.error;
        draft.highlights.menu.error = action.error;
        break;
      case SAVE_HIGHLIGHTS_BUILDER.START:
        draft.highlights.builder.loading = true;
        draft.highlights.fields.error = '';
        break;
      case SAVE_HIGHLIGHTS_BUILDER.SUCCESS:
        draft.highlights.builder.loading = false;
        draft.highlights.builder.data = action.payload.data.template;
        draft.highlights.isHighlightsInitialSetup = action.payload.data.isHighlightsSetup;
        break;
      case SAVE_HIGHLIGHTS_BUILDER.ERROR:
        draft.highlights.fields.loading = false;
        draft.highlights.fields.error = action.error;
        break;
      case UPDATE_SELECTED_FIELDS_COUNT:
        draft.highlights.selectedFieldsCount = action.payload.data;
        break;
      case UPDATE_CONVERSATION_LIST_FILTERS:
        draft.conversationList.filters = action.payload.data;
        break;
      case GET_ATTACHMENTS.START:
        draft.attachments.loading = true;
        draft.attachments.data = { visitorAttachments: [], accountAttachments: [] };
        break;
      case GET_ATTACHMENTS.SUCCESS:
        draft.attachments.loading = false;
        draft.attachments.data = action.payload.data;
        break;
      case GET_ATTACHMENTS.ERROR:
        draft.attachments.loading = false;
        draft.attachments.error = '';
        break;
      case UPDATE_ATTACHMENTS:
        draft.attachments.data = {
          ...state.attachments.data,
          visitorAttachments: [
            ...state.attachments.data.visitorAttachments,
            ...action.payload.files,
          ],
        };
        break;
      case UPDATE_UPLOAD_PROGRESS.SUCCESS:
        draft.attachments.uploadProgress = {
          ...state.attachments.uploadProgress,
          [action.payload.data.currentConversationId]: action.payload.data.percentage,
        };
        break;
      case UPDATE_UPLOAD_PROGRESS.ERROR:
        draft.attachments.uploadProgress = {
          ...state.attachments.uploadProgress,
          [action.payload.data.currentConversationId]: 0,
        };
        break;
    }
  });
};

export default chatReducer;
